import { toast } from "sonner";

import type {
  TournamentChaseWinPrizeNotificationSchema,
  TournamentResetWorstPrizeNotificationSchema,
} from "@/types";
import { ChaseToast } from "@/components/notification/chase-toast";
import { useAppStore } from "@/stores";

/**
 * The two chase notifications the dashboard shows (#4041): the player's own rung win
 * (`tournament_chase_win_prize`, the prize title arrives server-formatted and translated) and
 * the run being reset because the spins ran past the last rung still open
 * (`tournament_reset_worst_prize`). Both are toasted and acked on receipt — informational, like
 * the jackpot win; nothing gates on the player seeing them, and the ladder's won rung is on the
 * progress stream regardless.
 */
export type ChaseNotificationSchema =
  | TournamentChaseWinPrizeNotificationSchema
  | TournamentResetWorstPrizeNotificationSchema;

const CHASE_NOTIFICATION_TYPES = new Set([
  "tournament_chase_win_prize",
  "tournament_reset_worst_prize",
]);

export function isChaseNotification(
  notification: { type?: string } & object
): notification is ChaseNotificationSchema {
  return CHASE_NOTIFICATION_TYPES.has(notification.type ?? "");
}

/** Narrowed by hand — the generated `type` is optional with an index signature (see
 *  `mission-notifications`), so an inline check would leave every field `unknown`. */
export function isChaseWinNotification(
  notification: ChaseNotificationSchema
): notification is TournamentChaseWinPrizeNotificationSchema {
  return notification.type === "tournament_chase_win_prize";
}

/** A row older than this on the subscribe page is a reconnect replaying history, not news
 *  (the same window as the mission and jackpot toasts). */
const BACKLOG_FRESHNESS_MS = 30_000;

export function showChaseNotifications(
  notifications: ChaseNotificationSchema[],
  initial: boolean
): void {
  const state = useAppStore.getState();
  const unseen = notifications.filter(
    (n) => !(n.id in state.seenNotificationIds)
  );
  if (unseen.length === 0) return;
  state.markNotificationsSeen(unseen.map((n) => n.id));

  const cutoff = Date.now() - BACKLOG_FRESHNESS_MS;
  for (const notification of unseen) {
    if (initial && new Date(notification.created_at).getTime() < cutoff) {
      continue;
    }
    toast.custom((id) => (
      <ChaseToast
        notification={notification}
        onDismiss={() => toast.dismiss(id)}
      />
    ));
  }
}
